import { useVirtualizer } from '@tanstack/react-virtual';
import { FC, useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Checkbox } from '@mint-lib/tw-components';
import {
  ComboboxInput,
  ComboboxInputHelperText,
  ComboboxInputLabel,
  ComboboxInputRoot,
  ComboboxItem,
  ComboboxMenu,
} from '@mint-lib/tw-components/patterns';

import type { OptionItem } from '../types/shared.js';

type Props = {
  id: string;
  value: OptionItem[];
  options: OptionItem[];
  label?: string;
  labelTooltip?: string;
  a11yLabel: string;
  onChange: (value: OptionItem[]) => void;
  disabled?: boolean;
  helperText?: string;
  error?: boolean;
};

const ROW_HEIGHT_PX = 40;
const OVERSCAN_COUNT = 5;

const MultiSelectWithSearch: FC<Props> = ({
  id,
  value,
  options,
  label,
  labelTooltip,
  a11yLabel,
  onChange,
  disabled = false,
  helperText,
  error = false,
}) => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit_v2');

  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [searchValue, setSearchValue] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchValue) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [options, searchValue]);
  const displayValue = value.map((item) => item.label).join(', ');

  const listRef = useCallback((node: HTMLDivElement | null) => {
    const parent: HTMLDivElement | null =
      node?.closest('[data-slot="command-list"]') ?? null;
    setScrollElement(parent);
  }, []);

  const rowVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ROW_HEIGHT_PX,
    overscan: OVERSCAN_COUNT,
  });

  const handleOpenChange = (open: boolean) => {
    if (open) {
      requestAnimationFrame(() => {
        scrollElement?.scrollTo({ top: 0 });
        rowVirtualizer.scrollToOffset(0);
      });
    }

    setSearchValue('');
  };

  const handleSelect = (
    selectedValue: string,
    option: OptionItem,
    isSelected: boolean,
  ) => {
    const selectedOptionList = isSelected
      ? value.filter((item) => item.value !== selectedValue)
      : [...value, option];

    onChange(selectedOptionList);
  };

  return (
    <ComboboxInputRoot>
      {label ? (
        <ComboboxInputLabel
          htmlFor={id}
          label={label}
          infoIconWithTooltip={labelTooltip}
        />
      ) : null}
      <ComboboxInput
        inputProps={{
          id,
          autoComplete: 'off',
          'data-dd-action-name': a11yLabel,
        }}
        size="medium"
        value={displayValue}
        placeholder={t('Select')}
        onOpenChange={handleOpenChange}
        disabled={disabled}
        color={error ? 'error' : 'default'}
      >
        <ComboboxMenu
          withSearch
          emptyMessage={t('No options found')}
          shouldFilter={false}
          onValueChange={setSearchValue}
        >
          <div
            ref={listRef}
            className="relative w-full"
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualItem, index) => {
              const option = filteredOptions[virtualItem.index];
              const isSelectedOption = value.some(
                (item) => item.value === option.value,
              );
              return (
                <ComboboxItem
                  key={option.value}
                  data-dd-action-name={`Select option ${index + 1}`}
                  className="absolute top-0 left-0 w-full"
                  value={option.value}
                  selected={isSelectedOption}
                  onSelect={(val) =>
                    handleSelect(val, option, isSelectedOption)
                  }
                  keywords={[option.label]}
                  style={{
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <span
                    key={option.value}
                    className="flex items-center gap-2"
                    tabIndex={-1}
                  >
                    <Checkbox
                      key={isSelectedOption ? 'checked' : 'unchecked'}
                      className="pointer-events-none"
                      checked={isSelectedOption}
                    />
                    {option.label}
                  </span>
                </ComboboxItem>
              );
            })}
          </div>
        </ComboboxMenu>
      </ComboboxInput>
      {helperText ? (
        <ComboboxInputHelperText
          color={error ? 'error' : 'default'}
          helperText={helperText}
        />
      ) : null}
    </ComboboxInputRoot>
  );
};

export default MultiSelectWithSearch;
